import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FolderOpen } from "lucide-react";
import { Badge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { STAGES, STAGE_COLORS } from "@/lib/constants";
import { formatDateTime } from "@/lib/dates";
import { getPraticheRete, isAccessoSospeso } from "@/api/rete";
import type { PraticaRete } from "@/types/rete";
import { AccessoSospeso } from "./AccessoSospeso";
import { CHIAVE_AGENZIA, FiltroAgenzia } from "./FiltroAgenzia";
import { PAGE_SIZE, Paginazione, paginaDaUrl } from "./Paginazione";

const CAMPO = "min-h-[44px] px-3 py-2 border border-border rounded-lg text-sm bg-card";

export function PraticheRete() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const agenzia = sp.get(CHIAVE_AGENZIA) ?? "";
  const stato = sp.get("stato") ?? "";
  const q = sp.get("q") ?? "";
  const pagina = paginaDaUrl(sp);
  const [pendingSearch, setPendingSearch] = useState(q);
  const [items, setItems] = useState<PraticaRete[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPraticheRete({
      limit: PAGE_SIZE,
      offset: pagina * PAGE_SIZE,
      stage: stato || null,
      q: q || undefined,
      agenzia_id: agenzia || null,
    })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
      })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else setError("Impossibile caricare le pratiche.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agenzia, stato, q, pagina]);

  /** Un filtro cambia nell'URL e la lista riparte dalla prima pagina. */
  function impostaFiltro(chiave: string, valore: string) {
    setSp((prev) => {
      if (valore) prev.set(chiave, valore);
      else prev.delete(chiave);
      prev.delete("pagina");
      return prev;
    });
  }

  function apri(id: string) {
    navigate(`/rete/pratiche/${encodeURIComponent(id)}`);
  }

  if (sospeso) return <AccessoSospeso />;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-text">Pratiche della rete</h2>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <FiltroAgenzia />
        <select
          aria-label="Stato"
          value={stato}
          onChange={(e) => impostaFiltro("stato", e.target.value)}
          className={CAMPO}
        >
          <option value="">Tutti gli stati</option>
          {STAGES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            impostaFiltro("q", pendingSearch.trim());
          }}
          className="flex gap-2 flex-1"
        >
          <input
            type="search"
            aria-label="Cerca"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            placeholder="Cliente o pratica"
            className={`${CAMPO} flex-1 min-w-0`}
          />
          <button type="submit" className="min-h-[44px] px-4 bg-brand text-white rounded-lg text-sm flex-shrink-0">
            Cerca
          </button>
        </form>
      </div>

      {error ? (
        <ErrorState message={error} />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : items.length === 0 ? (
        <EmptyState icon={<FolderOpen size={26} className="text-brand" />} title="Nessuna pratica" />
      ) : (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pratica</TableHead>
                  <TableHead className="hidden md:table-cell">Cliente</TableHead>
                  <TableHead>Agenzia</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="hidden md:table-cell">Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => {
                  const color = STAGE_COLORS[c.stage] ?? STAGE_COLORS["Bozza"];
                  return (
                    <TableRow
                      key={c.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => apri(c.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          apri(c.id);
                        }
                      }}
                      className="cursor-pointer [&_td]:py-3"
                    >
                      <TableCell className="font-medium text-text">{c.name}</TableCell>
                      <TableCell className="hidden md:table-cell">{c.cliente_name ?? "—"}</TableCell>
                      <TableCell>{c.agenzia_nome ?? "—"}</TableCell>
                      <TableCell>
                        <Badge bg={color.bg} text={color.text}>{c.stage}</Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell tabular-nums text-muted-foreground">
                        {formatDateTime(c.created_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Paginazione total={total} loading={loading} />
    </div>
  );
}
